import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Instagram, Youtube, Facebook } from 'lucide-react';
import { managerBaseUrl } from '../../constant/constant';
// Disabled while there's no buyer-facing Apsession app to install — see the
// commented-out render below. Restore both lines together when the app ships.
// import FloatingFooter from './FloatingFooter';

const useWindowSize = () => {
  const [size, setSize] = useState([window.innerWidth, window.innerHeight]);

  useEffect(() => {
    const handleResize = () => setSize([window.innerWidth, window.innerHeight]);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return size;
};

// The event manager is a separate app on its own subdomain (hash-routed), so
// those links stay real cross-origin navigations rather than router links.
//
// Configured through VITE_MANAGER_BASE_URL rather than hardcoded here, so a
// staging build can point these at a staging manager instead of sending
// testers to the live one. See constant/constant.js for the fallback.
const MANAGER_BASE = managerBaseUrl;

const LINK_STYLE = { color: '#9ca3af', fontWeight: 500, transition: 'color 0.2s ease' };
const hoverOn = (e) => { e.currentTarget.style.color = '#ffea00'; };
const hoverOff = (e) => { e.currentTarget.style.color = '#9ca3af'; };

/*
 * One footer link. `to` routes inside the SPA (client-side, no reload); `href`
 * leaves the app and opens in a new tab. Wrapping this up keeps the yellow
 * hover behaviour in a single place instead of repeated on every anchor.
 */
const FooterLink = ({ to, href, children }) => {
  if (to) {
    return (
      <Link to={to} style={LINK_STYLE} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" style={LINK_STYLE} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
      {children}
    </a>
  );
};

const FooterColumn = ({ title, links }) => (
  <div>
    <h4
      className="text-xl mb-6 text-white tracking-wide"
      ref={(el) => { if (el) el.style.setProperty('font-weight', '900', 'important'); }}
    >
      {title}
    </h4>
    <ul className="space-y-4 text-gray-400 font-medium list-none" style={{ padding: 0, color: '#9ca3af', fontWeight: 500 }}>
      {links.map((link) => (
        <li key={link.label}>
          <FooterLink to={link.to} href={link.href}>{link.label}</FooterLink>
        </li>
      ))}
    </ul>
  </div>
);

const EXPLORE_LINKS = [
  { label: 'Discover Events', to: '/' },
  { label: 'Find My Ticket', to: '/lookup' },
  { label: 'My Tickets', to: '/my-tickets' },
];

const ORGANIZER_LINKS = [
  { label: 'Create an event', href: `${MANAGER_BASE}/event-setup` },
  { label: 'How it works', href: `${MANAGER_BASE}/home` },
  { label: 'Pricing', href: `${MANAGER_BASE}/pricing` },
  { label: 'Event Manager', href: `${MANAGER_BASE}/dashboard/overview` },
  { label: 'Organizer Agreement', to: '/organizer-agreement' },
];

// The signed legal documents, on every page as required.
const LEGAL_LINKS = [
  { label: 'Privacy Policy', to: '/privacy-policy' },
  { label: 'Terms of Service', to: '/terms-of-service' },
  { label: 'Organizer Agreement', to: '/organizer-agreement' },
];

export default function Footer() {
  const [width] = useWindowSize();

  return (
    <>
      {/* Mobile "Install Apsession" bottom banner. Hidden for now: the user
          app it links to (App Store id802888811 /
          com.apsession.air.apsessionuser) isn't available, so the Install
          button led nowhere useful. Uncomment this and the import above to
          bring it back. */}
      {/* {width < 577 && <FloatingFooter />} */}
      <footer
        className="bg-[#0a0a0a] text-white pt-20 pb-8"
        style={{
          borderTopWidth: "8px",
          borderTopStyle: "solid",
          borderTopColor: "#ffea00",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            <div>
              <img src="https://apsession.s3.us-east-1.amazonaws.com/Apsession+won+no+tag+white.png" alt="Apsession" className="h-10 w-auto mb-8 object-contain" />
              <p className="text-gray-400 mb-6 font-medium">Your global platform for the best events, nightlife, and cultural experiences.</p>
              <div className="flex items-center space-x-4">
                <a href="https://www.instagram.com/apsession/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-[#ffea00] hover:text-black rounded-full flex items-center justify-center transition-colors text-white shadow-sm hover:-translate-y-1">
                  <Instagram size={18} />
                </a>
                <a href="https://www.youtube.com/@ApsessionMobile" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-[#ffea00] hover:text-black rounded-full flex items-center justify-center transition-colors text-white shadow-sm hover:-translate-y-1">
                  <Youtube size={18} />
                </a>
                <a href="https://www.facebook.com/Apsession" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-[#ffea00] hover:text-black rounded-full flex items-center justify-center transition-colors text-white shadow-sm hover:-translate-y-1">
                  <Facebook size={18} />
                </a>
                <a href="https://wa.me/18768331945" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-[#ffea00] rounded-full flex items-center justify-center transition-colors shadow-sm hover:-translate-y-1 group">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp" className="w-[18px] h-[18px] group-hover:brightness-0 transition-all" />
                </a>
              </div>
            </div>

            <FooterColumn title="Explore" links={EXPLORE_LINKS} />
            <FooterColumn title="Organizers" links={ORGANIZER_LINKS} />

            <div>
              <h4
                className="text-xl mb-6 text-white tracking-wide"
                ref={(el) => { if (el) el.style.setProperty('font-weight', '900', 'important'); }}
              >
                Get in Touch
              </h4>
              <ul className="space-y-5 text-gray-400 font-medium list-none" style={{ padding: 0, color: '#9ca3af', fontWeight: 500 }}>
                <li>
                  <a
                    href="mailto:support@apsession.com"
                    className="flex items-center"
                    style={LINK_STYLE}
                    onMouseEnter={hoverOn}
                    onMouseLeave={hoverOff}
                  >
                    <Mail size={18} className="mr-3 text-[#ffea00] flex-shrink-0" /> support@apsession.com
                  </a>
                </li>
                <li className="flex items-center">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/0/0a/Flag_of_Jamaica.svg" alt="Jamaica" className="w-6 h-4 object-cover mr-3 rounded-sm shadow-sm flex-shrink-0" />
                  <span>876-833-1945</span>
                </li>
                <li className="flex items-center">
                  <img src="https://upload.wikimedia.org/wikipedia/en/a/a4/Flag_of_the_United_States.svg" alt="United States" className="w-6 h-4 object-cover mr-3 rounded-sm shadow-sm flex-shrink-0" />
                  <span>919-335-6856</span>
                </li>
                <li>
                  <a
                    href="https://wa.me/18768331945"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center pt-1 group"
                    style={LINK_STYLE}
                    onMouseEnter={hoverOn}
                    onMouseLeave={hoverOff}
                  >
                    <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp" className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform flex-shrink-0" />
                    <span className="leading-tight">WhatsApp chat<br />& calls available</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-800 text-sm font-medium text-gray-500" style={{ borderTop: '1px solid #1f2937', fontWeight: 500 }}  >
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="m-0 text-center sm:text-left">&copy; {new Date().getFullYear()} Apsession. All rights reserved. </p>
              <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 list-none m-0" style={{ padding: 0 }}>
                {LEGAL_LINKS.map((link) => (
                  <li key={link.to}>
                    <FooterLink to={link.to}>{link.label}</FooterLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
